import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  type Observable,
  of,
  switchMap,
} from 'rxjs';

import { type ContestModel } from '../../models/Contest';
import { type PoolModel } from '../../models/Pool';

import { ContestService } from './contest.service';
import { PoolService } from './pool.service';

export interface BreadcrumbItem {
  label?: string | Observable<string>;
  url: string;
  icon?: string;
  labelKey?: string;
  labelParams?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly _breadcrumbs$: BehaviorSubject<BreadcrumbItem[]> = new BehaviorSubject<
    BreadcrumbItem[]
  >([]);

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translocoService = inject(TranslocoService);
  private readonly poolService = inject(PoolService);
  private readonly contestService = inject(ContestService);

  // =============================================================================
  // Public Properties
  // =============================================================================

  get breadcrumbs$(): Observable<BreadcrumbItem[]> {
    return this._breadcrumbs$.asObservable();
  }

  // =============================================================================
  // Constructor
  // =============================================================================

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        distinctUntilChanged(),
        switchMap(() => this.buildBreadcrumbs(this.activatedRoute.root)),
      )
      .subscribe((breadcrumbs: BreadcrumbItem[]) => {
        this._breadcrumbs$.next(breadcrumbs);
      });
  }

  // =============================================================================
  // Private Methods
  // =============================================================================

  private buildBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: BreadcrumbItem[] = [],
  ): Observable<BreadcrumbItem[]> {
    const child = route.firstChild;
    if (!child) return of(breadcrumbs);

    const nextUrl = this.getNextUrl(child, url);
    this.addBreadcrumbIfDefined(child, nextUrl, breadcrumbs);

    return this.buildBreadcrumbs(child, nextUrl, breadcrumbs);
  }

  private getNextUrl(child: ActivatedRoute, url: string): string {
    const segment = child.snapshot.url.map((s) => s.path).join('/');
    return segment ? `${url}/${segment}` : url;
  }

  private addBreadcrumbIfDefined(
    child: ActivatedRoute,
    url: string,
    breadcrumbs: BreadcrumbItem[],
  ): void {
    const labelKey = child.routeConfig?.data?.['breadcrumb'] as string | undefined;
    if (!labelKey) return;

    const breadcrumbLabel$ = this.resolveBreadcrumbLabel(child, labelKey);
    const breadcrumb = this.createBreadcrumb(breadcrumbLabel$, url, child);

    const last = breadcrumbs[breadcrumbs.length - 1];
    if (!last || last.url !== breadcrumb.url || last.labelKey !== breadcrumb.labelKey) {
      breadcrumbs.push(breadcrumb);
    }
  }

  private createBreadcrumb(
    label$: Observable<string>,
    url: string,
    child: ActivatedRoute,
  ): BreadcrumbItem {
    const labelKey = child.routeConfig?.data?.['breadcrumb'] as string;
    const icon = child.routeConfig?.data?.['icon'] as string | undefined;
    return {
      label: label$,
      url: url,
      icon: icon,
      labelKey: ['breadcrumb.contestWith', 'breadcrumb.scoreWith'].includes(labelKey)
        ? undefined
        : labelKey,
    };
  }

  private resolveBreadcrumbLabel(child: ActivatedRoute, labelKey: string): Observable<string> {
    const contestId = child.snapshot.paramMap.get('contestId');
    const poolId = child.snapshot.paramMap.get('poolId');

    if (labelKey === 'breadcrumb.contestWith' && contestId) {
      return this.contestService.getContestById(contestId).pipe(
        map((contest: ContestModel) => (contest.name || contestId) as string),
        catchError(() => of(contestId as string)),
      );
    }

    if (labelKey === 'breadcrumb.scoreWith' && poolId) {
      return this.poolService.getPoolByIdOrName(poolId).pipe(
        map((pool: PoolModel) => (pool.name || poolId) as string),
        catchError(() => of(poolId as string)),
        switchMap(
          (name: string) =>
            this.translocoService.selectTranslate(labelKey, {
              poolName: name,
            }) as Observable<string>,
        ),
      );
    }

    return this.translocoService.selectTranslate(labelKey) as Observable<string>;
  }
}
